import React, { useEffect, useState } from 'react';
import { R, ft, mono } from '../lib/ui';
import type { Column, Rec } from '../lib/api';
import type { Sel } from './Grid';
import { isFormula } from '../lib/formula/engine';

interface Props {
  columns: Column[];
  records: Rec[];
  sel: Sel | null;
  onPatch: (records: Rec[]) => void;
  canEdit: boolean;
}

export function autoFixFormula(val: string): string {
  let res = val.trim();
  if (!res) return res;

  // Если похоже на функцию, например СУММ( или SUM(, но нет =, добавляем =
  if (/^[A-Za-zА-Яа-яЁё_][A-Za-zА-Яа-яЁё0-9_.]*\s*\(/.test(res)) {
    res = '=' + res;
  }

  // Закрываем скобки, если их не хватает
  const openCount = (res.match(/\(/g) || []).length;
  const closeCount = (res.match(/\)/g) || []).length;
  if (openCount > closeCount && res.startsWith('=')) {
    res = res + ')'.repeat(openCount - closeCount);
  }

  return res;
}

export function FormulaBar({ columns, records, sel, onPatch, canEdit }: Props) {
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);

  const activeRec = sel ? records[sel.r1] : null;
  const activeCol = sel ? columns[sel.c1] : null;
  const rawValue = activeRec && activeCol ? activeRec.fields[activeCol.fields.colId] : null;
  const displayValue = rawValue === null || rawValue === undefined ? '' : String(rawValue);

  useEffect(() => {
    if (!focused) {
      setValue(displayValue);
    }
  }, [displayValue, focused]);

  const commit = (val: string) => {
    if (!activeRec || !activeCol || !canEdit) return;
    const fixed = autoFixFormula(val);
    if (fixed !== String(rawValue ?? '')) {
      onPatch([{ id: activeRec.id, fields: { [activeCol.fields.colId]: fixed } }]);
    }
    setValue(fixed);
  };

  const handleBlur = () => {
    setFocused(false);
    commit(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLElement).blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setValue(displayValue);
      (e.target as HTMLElement).blur();
    }
  };

  const handleFxClick = () => {
    if (!canEdit || !activeRec || !activeCol) return;
    if (!value.trimStart().startsWith('=')) {
      const next = '=' + value;
      setValue(next);
      commit(next);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        height: 32,
        background: R.bg,
        borderBottom: ft.hair,
        flex: '0 0 auto',
      }}
    >
      <button
        disabled={!canEdit || !sel}
        onClick={handleFxClick}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '0 8px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: mono,
          fontWeight: 'bold',
          color: R.textMuted,
          cursor: canEdit && sel ? 'pointer' : 'default',
          opacity: canEdit && sel ? 1 : 0.5,
        }}
        title="Вставить формулу"
        onMouseEnter={(e) => {
          if (canEdit && sel) e.currentTarget.style.color = R.accent;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = R.textMuted;
        }}
      >
        fx
      </button>

      <div style={{ width: 1, height: 16, background: R.border, margin: '0 8px' }} />

      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={!canEdit || !sel}
        placeholder={sel ? 'Введите значение или =формулу' : ''}
        style={{
          flex: 1,
          height: '100%',
          border: 'none',
          background: 'transparent',
          outline: 'none',
          fontFamily: value.trimStart().startsWith('=') ? mono : 'inherit',
          fontSize: 13,
          color: R.text,
          padding: '0 4px',
        }}
      />
    </div>
  );
}
